import { useState } from 'react';
import Modal from '../components/Modal.tsx';
import CaseStudyPicker from './CaseStudyPicker.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { confirmStagingSpend, stagingAckHeaders } from '../lib/stagingGuard.ts';
import type { CaseStudyCard, CaseStudyImportResponse } from '../../shared/api.ts';

/**
 * "Import a case study" — the shelf's other way to make an item.
 *
 * Two steps, and the second one is a button: pick a case study (which fetches
 * the page and downloads its pictures and clips), then import (which starts the
 * section build that writes the slides). It deliberately asks nothing else. The
 * person picked a published piece of work, not a deck outline, so a form of
 * length and fidelity questions would be asking them to answer for the shelf
 * what the shelf should answer for itself; the item's name and category are
 * both editable afterwards from its own menu.
 *
 * The modal closes as soon as the build starts. The item is already on the
 * shelf by then — the section is created before the first slide is written —
 * so the grid, not a progress bar in here, is where it fills in.
 */

type Props = {
  /** Closes the modal. */
  onCancel: () => void;
  /** The build has started: `itemKey` is already on the shelf, filling. */
  onStarted: (started: { jobId: string; itemKey: string; name: string; category: string }) => void;
};

type ImportStarted = { jobId: string; itemKey: string; name: string; category: string };

export default function ImportCaseStudyModal({ onCancel, onStarted }: Props) {
  const [picked, setPicked] = useState<CaseStudyCard | null>(null);
  const [imported, setImported] = useState<CaseStudyImportResponse | null>(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  async function pick(card: CaseStudyCard) {
    setPicked(card);
    setImported(null);
    setReadError(null);
    setStartError(null);
    setReading(true);
    try {
      const res = await api.post<CaseStudyImportResponse>(`/api/case-studies/${card.slug}/import`);
      if (!res) throw new Error('The case study could not be read.');
      setImported(res);
    } catch (err) {
      setReadError(errorMessage(err));
    } finally {
      setReading(false);
    }
  }

  function clear() {
    setPicked(null);
    setImported(null);
    setReadError(null);
    setStartError(null);
  }

  async function start() {
    if (!picked || !imported) return;
    // The whole flow's one gated step: reading the page cost bandwidth, this
    // is the button that spends credits.
    if (!(await confirmStagingSpend('importing this case study'))) return;
    setStarting(true);
    setStartError(null);
    try {
      const res = await api.post<ImportStarted>(
        '/api/library/import-case-study',
        {
          slug: picked.slug,
          uploadId: imported.uploadId,
          // The client's name, which is how the shelf is read. Editable on the
          // item afterwards, so this is a starting point rather than a decision.
          name: picked.client || imported.title,
          imageNotes: imported.imageNotes,
        },
        { headers: await stagingAckHeaders() },
      );
      if (!res?.jobId) throw new Error('The import could not be started.');
      onStarted(res);
    } catch (err) {
      setStartError(errorMessage(err));
      setStarting(false);
    }
  }

  const ready = !!imported && !reading && !starting;

  return (
    <Modal
      ariaLabel="Import a case study"
      onClose={onCancel}
      className="modal--new-deck modal--import"
      // Refuses Escape and the backdrop once the build has started.
      escape={starting ? 'none' : 'bubble'}
      closeOnBackdrop={!starting}
    >
      <header className="modal__header new-deck__head">
        <h3 className="modal__title modal__title--sm" id="import-case-study-title">
          Import a case study
        </h3>
      </header>
      <div className="modal__body new-deck__body">
        <CaseStudyPicker
          picked={picked}
          imported={imported}
          importing={reading}
          importError={readError}
          onPick={(card) => void pick(card)}
          onClear={clear}
          disabled={starting}
        />
        {startError && (
          <p className="new-deck__notice" role="alert">
            {startError}
          </p>
        )}
        <p className="muted small">
          The copy, pictures and clips come from the page itself. Studio writes the slides, which takes a few minutes:
          the item appears on the shelf straight away and fills as they land.
        </p>
      </div>
      <footer className="modal__footer new-deck__foot">
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={starting}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" onClick={() => void start()} disabled={!ready}>
          {starting ? 'Starting…' : 'Import'}
        </button>
      </footer>
    </Modal>
  );
}
